import React from 'react';
import { Avatar, Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { PatientBoardConfig, PatientCardVM, phaseStyle } from './types';

const CHIP_SX = { fontWeight: 600, fontSize: '0.68rem', height: 20 };

interface Props {
  patient: PatientCardVM;
  config: PatientBoardConfig;
  // Extra badge(s) rendered alongside the phase/urgency chips (e.g. Coordinate's
  // referral-warning pill). Predict passes none.
  headerBadges?: React.ReactNode;
}

const PatientBoardHeader: React.FC<Props> = ({ patient, config, headerBadges }) => {
  const phStyle = phaseStyle(config, patient.phaseKey);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 2,
        bgcolor: '#fff',
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ px: 3, pt: 2, pb: 2.25, gap: 2 }}>
        {/* Identity block */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.25 }}>
              {patient.name}
            </Typography>
            <Chip
              label={phStyle.label}
              size="small"
              sx={{ ...CHIP_SX, bgcolor: phStyle.bgcolor, color: phStyle.color }}
            />
            {patient.urgency && (
              <Chip
                label={patient.urgency.label}
                size="small"
                sx={{ ...CHIP_SX, bgcolor: patient.urgency.bgcolor, color: patient.urgency.color }}
              />
            )}
            {headerBadges}
          </Stack>

          {patient.headerInfoLines.map((line, i) => (
            <Typography
              key={i}
              variant="body2"
              color="text.secondary"
              sx={{ mb: i < patient.headerInfoLines.length - 1 ? 0.5 : 0 }}
            >
              {line}
            </Typography>
          ))}
        </Box>

        {/* Assignment: avatar when assigned, Take button when unassigned */}
        <Box sx={{ flexShrink: 0 }}>
          {patient.assignedToName ? (
            <Tooltip title={patient.assignedToName} arrow>
              <Avatar sx={{ width: 40, height: 40, fontSize: '0.95rem', bgcolor: 'primary.main' }}>
                {patient.assignedToInitials}
              </Avatar>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => config.onTake(patient.id)}
              sx={{ minWidth: 80, fontSize: '0.78rem', textTransform: 'none', boxShadow: 'none' }}
            >
              Take
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default PatientBoardHeader;
